/** THE SHARED PAGING (ported 2026-10-18 from the medrec console's lib/list-page.ts, its providers-paging lane, PR #169, under Rod's 10-11
 *  rule that the two consoles stay matched): the page in a list's address, the page cut, the "Showing 1-100 of 193" words and the pager's
 *  words, one version for every list that pages. The pager itself is `list/Pager.tsx`.
 *
 *  THE PAGE IN THE ADDRESS (`?page=3`), so a refresh or a link keeps it: page 1 writes nothing, as a list's default sort writes nothing. A
 *  sort, a filter, a search or a tab change starts again at page 1 (`firstPage`): it reorders or narrows every row, so the page on screen
 *  means nothing after it. A list sorts and narrows ALL its rows first and only then cuts the page, so a sort never orders just the rows on
 *  screen. Sweet Claims' one paged list, All unopened, is cut on the server (core unopened.ts unopenedPage); `pageSpan` reads the page it
 *  sent back into the same words. Pure. */

/** The page's key in a list's address (the medrec console's key too). */
export const PAGE_PARAM = 'page';

/** The page the address names: a whole number from 1; none, or anything else, is page 1 (the server, or `pageOf`, keeps it inside the
 *  pages there are). */
export function pageParamOf(params: URLSearchParams): number {
  const v = params.get(PAGE_PARAM);
  if (v === null || !/^[1-9][0-9]*$/.test(v)) return 1;
  const n = Number(v);
  return Number.isSafeInteger(n) ? n : 1;
}

/** THE ADDRESS FOR A PAGE: every other key kept as it is, then the page; page 1 (or anything that is not a page) writes none. */
export function writePage(params: URLSearchParams, page: number): URLSearchParams {
  const out = new URLSearchParams();
  params.forEach((v, k) => { if (k !== PAGE_PARAM) out.append(k, v); });
  if (Number.isSafeInteger(page) && page > 1) out.append(PAGE_PARAM, String(page));
  return out;
}

/** Back to page 1: a sort, a filter, a search or a tab change changed what the list holds. */
export const firstPage = (params: URLSearchParams): URLSearchParams => writePage(params, 1);

/** THE PAGE OF ROWS ON SCREEN: at least one page, the page asked kept inside them, and the 1-based first and last row shown (0 and 0 with
 *  no row). */
export function pageOf<R>(rows: readonly R[], page: number, size: number): { rows: R[]; page: number; pages: number; from: number; to: number; total: number } {
  const total = rows.length;
  const pages = Math.max(1, Math.ceil(total / size));
  const at = Math.min(Math.max(1, Number.isFinite(page) ? Math.floor(page) : 1), pages);
  const start = (at - 1) * size;
  const shown = rows.slice(start, start + size);
  return { rows: shown, page: at, pages, from: total === 0 ? 0 : start + 1, to: total === 0 ? 0 : start + shown.length, total };
}

/** A SERVER'S PAGE in the same terms (Sweet Claims: All unopened is cut on the server): the 1-based first and last row shown, 0 and 0 with
 *  no row, from the page it sent back and the rows it holds. */
export function pageSpan(p: { page: number; pageSize: number; total: number; shown: number }): { from: number; to: number; total: number } {
  if (p.total === 0 || p.shown === 0) return { from: 0, to: 0, total: p.total };
  const from = (p.page - 1) * p.pageSize + 1;
  return { from, to: from + p.shown - 1, total: p.total };
}

/** THE PAGE A PRESS LEADS TO: from the page asked last (the address, already ahead while the next page loads, so two quick presses go two
 *  pages) when it is a page there is, else the page on screen; always inside the pages there are. */
export function pageAfter(move: 'first' | 'prev' | 'next' | 'last', asked: number, shown: number, pages: number): number {
  const from = asked >= 1 && asked <= pages ? asked : shown;
  const to = move === 'first' ? 1 : move === 'prev' ? from - 1 : move === 'next' ? from + 1 : pages;
  return Math.min(Math.max(1, to), Math.max(1, pages));
}

const en = (n: number): string => n.toLocaleString('en-US');

/** THE FOOTER'S COUNT: "Showing 101-193 of 193" (a plain hyphen, en-US numbers), "Showing 0 of 0" with no row. */
export function showingWords(p: { from: number; to: number; total: number }): string {
  return p.total === 0 ? 'Showing 0 of 0' : `Showing ${en(p.from)}-${en(p.to)} of ${en(p.total)}`;
}

/** THE PAGER'S WORDS: each button's name and hover, the page it is on (the words a screen reader hears on each move), the group's name,
 *  and the rows a page holds. */
export const PAGER_WORDS = Object.freeze({
  firstPage: 'First page', previousPage: 'Previous page', nextPage: 'Next page', lastPage: 'Last page',
  pageOfPages: (page: number, pages: number): string => `Page ${en(page)} of ${en(pages)}`,
  pages: 'Pages',
  perPage: (n: number): string => `${en(n)} per page`,
});
